/**
 * Les familles d'articles, et la suggestion de famille à la saisie.
 *
 * Le principe : la boutique a déjà encodé des milliers d'articles, et c'est
 * cette histoire qui fait autorité, pas un lexique inventé. Le serveur cherche
 * donc les articles déjà classés qui partagent des mots avec ce qu'on est en
 * train de taper, et propose leur catégorie majoritaire.
 *
 * Le lexique ci-dessous n'est qu'une amorce, pour que la suggestion fonctionne
 * avant que la base contienne assez d'historique.
 */
/**
 * Les familles du magasin, reprises telles quelles de l'ancien logiciel.
 *
 * Les codes sont ceux qui figurent depuis toujours sur les étiquettes et dans
 * la tête de l'équipe : ils ne changent pas. Seuls les libellés ont été rendus
 * lisibles — « ARTICLES MENAG. » devient « Articles ménagers » — le code
 * restant la clé qui fait foi, notamment à la reprise de l'ancienne base.
 *
 * L'ordre est celui de l'usage réel, mesuré sur les 337 127 articles encodés
 * depuis 2010 : les plus courantes en tête, pour que la grille de saisie tombe
 * juste du premier coup. Les familles « achat » ferment la marche : elles
 * décrivent un mode d'acquisition — le magasin achète au lieu de prendre en
 * dépôt — et non une nature d'objet.
 *
 * Cinq familles de l'ancien logiciel ne sont pas reprises, faute d'avoir jamais
 * servi : OOO (« ERREUR »), IDV, IVX, IDN et FSR.
 */
export const FAMILLES = [
    { code: 'DEC', libelle: 'Décoration', emoji: '🖼️' },
    { code: 'AME', libelle: 'Ameublement', emoji: '🪑' },
    { code: 'LIB', libelle: 'Librairie', emoji: '📚' },
    { code: 'JOU', libelle: 'Jouets', emoji: '🧸' },
    { code: 'COL', libelle: 'Collection', emoji: '🪙' },
    { code: 'ARM', libelle: 'Articles ménagers', emoji: '🍳' },
    { code: 'OUT', libelle: 'Outillage', emoji: '🔨' },
    { code: 'CAD', libelle: 'Cadres & toiles', emoji: '🎨' },
    { code: 'DVD', libelle: 'Films DVD', emoji: '📀' },
    { code: 'LUS', libelle: 'Lustrerie', emoji: '💡' },
    { code: 'VAI', libelle: 'Vaisselle', emoji: '🍽️' },
    { code: 'MAR', libelle: 'Maroquinerie', emoji: '👜' },
    { code: 'BD', libelle: 'BD', emoji: '📖' },
    { code: 'LOI', libelle: 'Loisirs', emoji: '🎣' },
    { code: 'JEV', libelle: 'Jeux vidéo & consoles', emoji: '🎮' },
    { code: 'CD', libelle: 'CD', emoji: '💿' },
    { code: 'DIS', libelle: 'Disques', emoji: '🎵' },
    { code: 'BIJ', libelle: 'Bijoux', emoji: '💍' },
    { code: 'RTH', libelle: 'Radio / TV / Hi-fi', emoji: '📻' },
    { code: 'HOR', libelle: 'Horlogerie', emoji: '⏰' },
    { code: 'PEL', libelle: 'Petit électro', emoji: '🔌' },
    { code: 'MIL', libelle: 'Militaria', emoji: '🎖️' },
    { code: 'VER', libelle: 'Verrerie', emoji: '🥂' },
    { code: 'MAI', libelle: 'Maison', emoji: '🏠' },
    { code: 'BLU', libelle: 'Blu-ray', emoji: '🎬' },
    { code: 'SPO', libelle: 'Sports', emoji: '🎿' },
    { code: 'ARP', libelle: 'Articles professionnels', emoji: '🧰' },
    { code: 'JAR', libelle: 'Jardin', emoji: '🌿' },
    { code: 'BUR', libelle: 'Bureau', emoji: '✏️' },
    { code: 'AUT', libelle: 'Auto & moto', emoji: '🚗' },
    { code: 'PHO', libelle: 'Photo', emoji: '📷' },
    { code: 'GEL', libelle: 'Gros électro', emoji: '🧺' },
    { code: 'MUS', libelle: 'Musique', emoji: '🎸' },
    { code: 'CHA', libelle: 'Chauffage', emoji: '🔥' },
    { code: 'INF', libelle: 'Informatique', emoji: '💻' },
    { code: 'LWY', libelle: 'Émaux de Longwy', emoji: '🏺' },
    { code: 'NOE', libelle: 'Noël', emoji: '🎄' },
    { code: 'TAP', libelle: 'Tapis', emoji: '🧶' },
    { code: 'BEB', libelle: 'Spécial bébé', emoji: '🍼' },
    { code: 'VET', libelle: 'Vêtements', emoji: '👗' },
    { code: 'PAR', libelle: 'Parfums', emoji: '🌸' },
    { code: 'BON', libelle: 'Bon cadeau', emoji: '🎁' },
    { code: 'DIV', libelle: 'Divers', emoji: '🏷️' },
    // ── Achats directs ────────────────────────────────────────────────────────
    { code: 'ADI', libelle: 'Achats divers', emoji: '🛒' },
    { code: 'AJV', libelle: 'Achat jeux vidéo', emoji: '🛒' },
    { code: 'ABR', libelle: 'Achat Blu-ray', emoji: '🛒' },
    { code: 'ALI', libelle: 'Achat livre', emoji: '🛒' },
    { code: 'ABD', libelle: 'Achat BD', emoji: '🛒' },
    { code: 'ACH', libelle: 'Achat', emoji: '🛒' },
    { code: 'ABI', libelle: 'Achat bijoux', emoji: '🛒' },
    { code: 'AAM', libelle: 'Achat ameublement', emoji: '🛒' },
    { code: 'DCG', libelle: 'Achat DVD & jeux', emoji: '🛒' },
];
/** Les libellés seuls, dans l'ordre d'affichage. */
export const CATEGORIES = FAMILLES.map((f) => f.libelle);
/** La famille de repli, quand rien ne permet de trancher. */
export const CATEGORIE_DEFAUT = 'Divers';
const PAR_LIBELLE = new Map(FAMILLES.map((f) => [f.libelle, f]));
const PAR_CODE = new Map(FAMILLES.map((f) => [f.code, f]));
/** Retrouve une famille par son libellé ou par son code — « DEC » comme « Décoration ». */
export function famille(cle) {
    return PAR_LIBELLE.get(cle) ?? PAR_CODE.get(cle.trim().toUpperCase());
}
/** « DEC » — le code court affiché sur l'étiquette et dans les listes. */
export function codeFamille(libelle) {
    return famille(libelle)?.code ?? '—';
}
/** Mots trop courants pour discriminer quoi que ce soit. */
const MOTS_VIDES = new Set([
    'AVEC', 'SANS', 'POUR', 'DANS', 'LES', 'DES', 'UNE', 'UN', 'LE', 'LA', 'DE', 'DU',
    'ET', 'EN', 'AU', 'AUX', 'SUR', 'PAR', 'PETIT', 'PETITE', 'GRAND', 'GRANDE',
    'LOT', 'PAIRE', 'ANCIEN', 'ANCIENNE', 'VINTAGE', 'BTE', 'BOITE',
]);
/**
 * Découpe une désignation en mots comparables : majuscules, sans accents,
 * sans ponctuation. « Fauteuil scandinave teck 1962 » → FAUTEUIL, SCANDINAVE, TECK.
 */
export function motsSignificatifs(designation) {
    return designation
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .filter((mot) => mot.length >= 3 && !MOTS_VIDES.has(mot) && !/^\d+$/.test(mot));
}
/**
 * Lexique d'amorce — sert tant que la base n'a pas d'historique comparable.
 *
 * Il n'est pas inventé : ces mots ont été extraits des 337 127 désignations
 * encodées par la boutique depuis 2010, en ne retenant pour chaque famille que
 * ceux qui y sont à la fois fréquents et concentrés. « ASSIETTE » ne vaut que
 * parce qu'il ne se promène pas dans les autres familles. Les mots trop
 * génériques que l'extraction avait ramassés au passage ont été écartés à la
 * main.
 *
 * Certaines associations surprennent mais sont l'usage réel du magasin : les
 * chaussures et les vestes sont rangées en maroquinerie, les vinyles en
 * « Disques » et les instruments en « Musique ».
 */
export const LEXIQUE_CATEGORIES = {
    Décoration: [
        'VASE', 'DECO', 'BOUGEOIR', 'CUIVRE', 'CENDRIER', 'GRES', 'PANIER', 'SCULPTURE',
        'RESINE', 'LAITON', 'ETAIN', 'CUITE', 'TERRE', 'CACHE', 'STATUETTE', 'PHOTOPHORE',
    ],
    Ameublement: [
        'TABLE', 'CHAISE', 'CHAISES', 'FAUTEUIL', 'SALON', 'CHENE', 'TIROIRS', 'TIROIR',
        'MEUBLE', 'TABOURET', 'ETAGERE', 'PORTES', 'ARMOIRE', 'COMMODE', 'BUFFET', 'CANAPE',
    ],
    Librairie: [
        'LIVRE', 'VOLUMES', 'REVUE', 'HISTOIRE', 'DICTIONNAIRE', 'LAROUSSE', 'ENCYCLOPEDIE',
        'TASCHEN', 'BIBLE', 'MISSEL', 'TOMES', 'VERNE', 'OEUVRES', 'ROMAN',
    ],
    Jouets: [
        'PLAYMOBIL', 'JOUET', 'CAMION', 'LEGO', 'EVEIL', 'TOYS', 'PUZZLE', 'DINKY', 'BARBIE',
        'VOITURES', 'VTECH', 'PELUCHE', 'POUPEE',
    ],
    Collection: [
        'ANCIENNES', 'TIMBRES', 'MONNAIE', 'MONNAIES', 'AUTOGRAPHE', 'FRANCS', 'BILLETS',
        'BILLET', 'POSTALES', 'POSTALE', 'BANQUE', 'ETHNIE', 'MEDAILLE',
    ],
    'Articles ménagers': [
        'CASSEROLE', 'COUVERCLE', 'TUPPERWARE', 'INOX', 'POELE', 'POELON', 'COUTEAUX',
        'COUVERTS', 'COUVERT', 'PASSOIRE', 'MOULE', 'HACHOIR', 'MENAGERE', 'THERMO',
    ],
    Outillage: [
        'OUTIL', 'SCIE', 'PINCE', 'MARTEAU', 'DECKER', 'TOURNEVIS', 'ETAU', 'FOREUSE',
        'FACOM', 'PONCEUSE', 'RABOT', 'PERCEUSE', 'ETABLI',
    ],
    'Cadres & toiles': [
        'CADRE', 'MIROIR', 'HUILE', 'CONTOUR', 'REPRO', 'TOILE', 'AQUARELLE', 'SIGNEE',
        'SIGNE', 'GRAVURE', 'AFFICHE', 'ENCADREMENT', 'LITHO', 'PANNEAU', 'TABLEAU',
    ],
    'Films DVD': [
        'SERIE', 'SAISON', 'INTEGRALE', 'FILMS', '3DVD', '5DVD', 'DVDX', '4DVD', '2DVD',
        '6DVD', 'EXPERTS',
    ],
    Lustrerie: [
        'LAMPE', 'LUSTRE', 'POSER', 'ABAT', 'JOUR', 'LAMPADAIRE', 'APPLIQUE', 'PLAFONNIER',
        'BRANCHES', 'SPOT', 'SPOTS', 'LUMINAIRE', 'RAMPE', 'PAMPILLES',
    ],
    Vaisselle: [
        'ASSIETTE', 'ASSIETTES', 'BOCH', 'TASSE', 'TASSES', 'SERVICE', 'VILLEROY', 'BOTANICA',
        'SOUPIERE', 'ACAPULCO', 'NAIF', 'SARREGUEMINE', 'OBERNAI', 'PORCELAINE',
    ],
    Maroquinerie: [
        'CHAUSSURE', 'LUNETTE', 'VESTE', 'PORTEFEUILLE', 'SAMSONITE', 'KIPLING', 'LONGCHAMP',
        'VUITTON', 'CARTABLE', 'GUESS', 'VALISE', 'CEINTURE',
    ],
    BD: [
        'DEDICACE', 'SERVAIS', 'BECASSINE', 'TIBET', 'HERGE', 'FLUPKE', 'BOBETTE',
        'CASTAFIORE', 'OTTOKAR',
    ],
    Loisirs: [
        'CAGE', 'PECHE', 'TRANSPORT', 'AQUARIUM', 'JUMELLE', 'JUMELLES', 'MOULINET',
        'HAMSTER', 'LITIERE', 'RONGEUR', 'VOLIERE', 'TERRARIUM', 'FERPLAST', 'EPUISETTE',
    ],
    'Jeux vidéo & consoles': [
        'NINTENDO', 'MANETTE', 'MANETTES', 'XBOX', 'GAME', 'MARIO', 'SEGA', 'ADVANCE',
        'GAMEBOY', 'FIFA', 'PLAYSTATION', 'ATARI', 'CONSOLE',
    ],
    Disques: [
        'VINYL', 'VINYLS', 'BEATLES', 'PICTURE', 'TOURS', 'ROLLING', 'FLOYD', 'PINK',
        'STONES', 'PRESSAGE', 'BOWIE', 'KISS', 'SPRINGSTEEN', 'VINYLE',
    ],
    Bijoux: [
        'COLLIER', 'BAGUE', 'PENDENTIF', 'BRACELET', 'ARGENT', 'OREILLE', 'FANTAISIE',
        'BOUCLE', 'BOUCLES', 'BROCHE', 'PERLE', 'PERLES', 'MANCHETTE', 'SWAROVSKI',
    ],
    'Radio / TV / Hi-fi': [
        'RADIO', 'LECTEUR', 'SONY', 'PLATINE', 'BAFFLES', 'BAFFLE', 'AMPLI', 'SAMSUNG',
        'HIFI', 'ECRAN', 'ENCEINTE', 'CASSETTE', 'TUNER',
    ],
    Horlogerie: [
        'MONTRE', 'HORLOGE', 'GOUSSET', 'QUARTZ', 'MECANIQUE', 'BALANCIER', 'REVEIL',
        'AUTOMATIC', 'SWATCH', 'CHRONO', 'RODANIA', 'JUNGHANS', 'WATCH', 'OMEGA',
    ],
    'Petit électro': [
        'MOULINEX', 'ASPIRATEUR', 'VAPEUR', 'SILVERCREST', 'GRILL', 'SEVERIN', 'KRUPS',
        'DOMO', 'FRITEUSE', 'MIXEUR', 'CENTRIFUGEUSE', 'MIXER', 'RACLETTE', 'PRIMO',
    ],
    Militaria: [
        'MILITAIRE', 'REICH', 'OBUS', 'BAIONNETTE', 'WWII', 'ALLEMANDE', 'FOURREAU',
        'MUNITION', 'KEPI', 'OFFICIER', 'INSIGNE', 'ARMEE', 'FOUILLE', 'CALOT',
    ],
    Verrerie: [
        'VERRES', 'COUPES', 'FLUTES', 'PORTO', 'LEFFE', 'BIERRE', 'CHIMAY', 'KWAK',
        'SPIEGELAU', 'CARAFFE', 'CISELES', 'CARAFE', 'CRISTAL',
    ],
    Maison: [
        'EVIER', 'DOUCHE', 'STORE', 'MOUSTIQUAIRE', 'PAILLASSON', 'TAIES', 'CINTRE', 'DRAP',
    ],
    'Blu-ray': ['BLURAY', 'STEELBOOK'],
    Sports: [
        'VELO', 'APPARTEMENT', 'BTWIN', 'KETTLER', 'DOMYOS', 'MUSCULATION', 'ELLIPTIQUE',
        'ALTERE', 'PUKY', 'JUMPERTREK', 'DRAISIENNE', 'DECATHLON', 'BOXE', 'HALTERE',
    ],
    'Articles professionnels': [
        'DEAMBULATEUR', 'ROULANTE', 'PROFESSIONNEL', 'PROFESSIONNELLE', 'BEQUILLE',
        'BEQUILLES', 'TRIBUNE', 'TENSIOMETRE',
    ],
    Jardin: ['TONDEUSE', 'PARASOL', 'SEMOIR', 'GAZON', 'TRANSAT', 'ARROSOIR', 'BROUETTE'],
    Bureau: [
        'STYLO', 'PERFORATRICE', 'PARKER', 'BUVARD', 'WATERMAN', 'LEITZ', 'DYMO', 'DAHLE',
        'PELIKAN', 'ROGNEUSE', 'FELLOWES',
    ],
    'Auto & moto': [
        'PNEUS', 'JANTES', 'RETROVISEUR', 'AUTORADIO', 'THULE', 'CALANDRE', 'ENJOLIVEURS',
    ],
    Photo: [
        'BOITIER', 'OBJECTIF', 'NIKON', 'KODAK', 'POLAROID', 'AGFA', '50MM', 'MONTURE',
        'PENTAX', 'BROWNIE', 'MINOLTA', 'YASHICA', 'ZEISS',
    ],
    'Gros électro': [
        'LAVER', 'CONGELATEUR', 'FRIGO', 'ZANUSSI', 'SECHOIR', 'SIEMENS', 'CONDENSATION',
        'MIELE', 'LAVE', 'ENCASTRABLE', 'WHIRLPOOL', 'BEKO',
    ],
    Musique: [
        'GUITARE', 'HARMONICA', 'DJEMBE', 'HOHNER', 'CLARINETTE', 'PARTITION', 'CLAIRON',
        'VIOLON', 'METRONOME', 'TAMBOURIN', 'STAGG', 'CITHARE', 'SYNTHE', 'ACOUSTIQUE',
    ],
    Chauffage: [
        'CHAUFFAGE', 'ALLUMAGE', 'RADIATEUR', 'BOULEAU', 'BUCHETTES', 'BUCHETTE', 'CHENET',
        'CHENETS', 'CHAUFFERETTE',
    ],
    Informatique: [
        'INFORMATIQUE', 'CLAVIER', 'IMPRIMANTE', 'LOGITECH', 'DELL', 'WIFI', 'IPAD',
        'WEBCAM', 'EPSON', 'ACER', 'ASUS',
    ],
    'Émaux de Longwy': [
        'LONGWY', 'EMAUX', 'RECOLLETS', 'REHAUSSE', 'POMMIER', 'CHEVALLIER', 'MIGNON',
    ],
    Noël: ['NOEL', 'CRECHE', 'GUIRLANDE'],
    Tapis: ['TAPIS', 'PERSE', 'IRANIEN', 'KILIM'],
    'Spécial bébé': ['BIBERON', 'BEABA', 'BABYPHONE', 'AVENT'],
};
/**
 * Suggestion issue du seul lexique — utilisée en repli.
 *
 * La comparaison se fait par préfixe et non par égalité : les désignations
 * réelles sont au pluriel — « Lot 12 assiettes Villeroy » — et un lexique qui
 * ne connaîtrait qu'« ASSIETTE » les manquerait toutes. On exige quatre
 * lettres pour qu'un préfixe court ne rattache pas n'importe quoi.
 */
export function suggestionLexicale(designation) {
    const mots = motsSignificatifs(designation);
    if (mots.length === 0)
        return null;
    const correspond = (motCle) => mots.some((mot) => mot === motCle || (motCle.length >= 4 && mot.startsWith(motCle)));
    let meilleure = null;
    let meilleurScore = 0;
    for (const [categorie, motsCles] of Object.entries(LEXIQUE_CATEGORIES)) {
        const score = motsCles.filter(correspond).length;
        if (score > meilleurScore) {
            meilleurScore = score;
            meilleure = categorie;
        }
    }
    return meilleure;
}
//# sourceMappingURL=categories.js.map